// Screen 6 · The plan, rendered "flow on screen, form on print":
//  - ON SCREEN: a clean stacked flow — each step full-width (no empty Student column), flexing with
//    content length; the Consolidation misconception + check sit in highlighted boxes. Mobile-first.
//  - ON PRINT: the paper-faithful GTIS form — the two-column Teacher's Lesson Content | Student's
//    Activities table (blank Student column for hand-annotation), compacted toward one page.
// Both are rendered from the SAME data and toggled by @media print (CSS can't morph one into the
// other). Empty steps read as friendly prompts on screen, and print BLANK on paper.
// Owner can Submit if a draft; the Lead can view content + approve/re-open (teacher+Lead tier).
// JOB-W01: a separate "Teaching record" panel — the owner confirms the lesson was taught (or
// withdraws that with a reason); the Lead reads the same state + history but cannot record it.
// Plan status (Submit/Approve) and the teaching record never change each other.

import { api, ApiError } from "../api";
import { esc } from "../dom";
import { navigate } from "../router";
import { planningWeekHref } from "../planning-source";
import { t } from "../i18n";
import { me } from "../state";
import { wireDraftDelete } from "../lesson-data";
import { contextBar } from "../components/nav";
import { pageHeader, toast } from "../ui";
import { booksCardHtml, refsFor } from "../lesson-refs";
import { richTextHtml } from "../rich-text-format";
import { loadResources, resourcesListHtml } from "../plan-resources-ui";
import { icon } from "../icons";

// Plans are written with light formatting (bold, italic, points, numbers — teacher review, 15 Oct 2026). The text
// column still holds plain text with a few markers; richTextHtml() escapes everything and draws only paragraphs,
// line breaks, lists, bold and italic. An old plain-text plan reads as before wherever it has none of those markers.
// Where it has them, they now format (rich-text-format.ts has the full rules): a line that starts "- ", "* ", "• ",
// "1. " or "1) " shows as a point, **word** shows in bold and *word* in italics, and a backslash before a star or
// another backslash is not shown (a stored "C:\\files" reads "C:\files").
const rich = (value: string): string => `<div class="rt-view">${richTextHtml(value)}</div>`;

interface RenderedCell {
  label: string;
  value: string | null;
  emptyPrompt: string;
}
interface RenderedStage {
  key: string;
  title: string;          // "1 · Class Admin"
  content: RenderedCell;  // the teacher's typed step text
  cells?: RenderedCell[]; // Consolidation: the misconception + check
}
interface PlanResponse {
  plan_id: string;
  lesson: { id: string; topic: string; class_name: string; class_id: string; term_id: string; week_no: number; status: string; approval_note: string | null };
  related?: {
    class: { id: string; name: string };
    week_no: number;
    siblings_href: string;
    calendar_href: string;
  };
  rendered: {
    objectives: RenderedCell;
    priorKnowledge: RenderedCell;
    stages: RenderedStage[];
    legacyProcedure: RenderedCell | null;
  };
  timing: { started_at: string | null; completed_at: string | null; duration_sec: number | null };
  can_submit: boolean;
  can_edit: boolean;
  lesson_id: string;
  can_approve: boolean;
  can_reopen: boolean;
  locked: boolean;
  content_changed: boolean;
}

// A labeled block (Objectives / Prior knowledge / legacy procedure). Empty → a faint prompt.
function cellBlock(cell: RenderedCell): string {
  const content = cell.value ? rich(cell.value) : `<p class="hint">${esc(cell.emptyPrompt)}</p>`;
  return `<div class="plan-field"><strong>${esc(cell.label)}</strong>${content}</div>`;
}

// ── Screen: one step as a full-width block (number in seal · title, content, then any cells) ──────
function stageScreen(s: RenderedStage): string {
  const [num, ...rest] = s.title.split(" · ");
  const title = `<span class="step-n">${esc(num ?? "")}</span> ${esc(rest.join(" · "))}`;
  const content = s.content.value
    ? `<div class="step-content">${rich(s.content.value)}</div>`
    : `<p class="step-content hint">${esc(s.content.emptyPrompt)}</p>`;
  const cells = (s.cells ?? [])
    .map((c) => {
      const body = c.value ? rich(c.value) : `<p class="hint">${esc(c.emptyPrompt)}</p>`;
      return `<div class="cell-box"><div class="cell-label">${esc(c.label)}</div>${body}</div>`;
    })
    .join("");
  return `<div class="step-block"><div class="step-title">${title}</div>${content}${cells}</div>`;
}

// ── Print: one step's Teacher cell — real content only (blank when empty, so a filed plan reads
//    clean and the blank Student column is room to annotate by hand). ─────────────────────────────
function teacherCellPrint(s: RenderedStage): string {
  const main = s.content.value ? rich(s.content.value) : "";
  const sub = (s.cells ?? [])
    .map((c) => (c.value ? `<div class="td-sub"><strong>${esc(c.label)}</strong>${rich(c.value)}</div>` : ""))
    .join("");
  return main + sub;
}

// A plain, un-ranked statement of effort — never a score or a comparison (the rails).
function timingLine(durationSec: number | null): string {
  if (durationSec === null) return "";
  const mins = Math.round(durationSec / 60);
  const text = mins < 1 ? t("view.planned_under_min") : t("view.planned_in", { m: mins });
  return `<p class="muted">${text}</p>`;
}

export async function renderPlanView(app: HTMLElement, params: Record<string, string>): Promise<void> {
  let data: PlanResponse;
  try {
    data = await api<PlanResponse>(`/api/plans/${encodeURIComponent(params.id!)}`);
  } catch (err) {
    // A plan opens for its writer and the Lead only. A teacher reaches someone else's plan when the Lead
    // moved a class to them after its first teacher planned on it (My plans lists the class's lessons).
    if (err instanceof ApiError && err.status === 403) {
      app.innerHTML = `${pageHeader({ title: "Another teacher's plan", sub: "Only the teacher who wrote this plan, and the Lead, can open it.", crumbs: [{ label: "My plans", href: "/plans" }, { label: "Plan" }] })}
        <p><a class="btn secondary" data-link href="/plans">Back to My plans</a></p>`;
      return;
    }
    throw err;
  }
  const { lesson, rendered } = data;
  // The Lead, looking at another teacher's DRAFT (owner ruling A1: the Lead can delete any draft), gets Delete here.
  const leadDraft = me()?.auth_role === "lead" && !data.can_edit && (lesson.status === "in_progress" || lesson.status === "not_started");

  // The stored lesson term is the authority. A bookmarked query cannot change which class-term
  // or planned week this exact plan opens; historical plans never default to the current term.
  const rel = data.related;
  const termQuery = `term_id=${encodeURIComponent(lesson.term_id)}`;
  const context = rel
    ? contextBar([
        { label: rel.class.name, href: `/classes/${encodeURIComponent(rel.class.id)}?${termQuery}` },
        { label: t("common.week_n", { n: rel.week_no }), href: `/classes/${encodeURIComponent(rel.class.id)}?${termQuery}&week=${rel.week_no}` },
        { label: t("nav.class_lessons"), href: `/classes/${encodeURIComponent(rel.class.id)}?${termQuery}&week=${rel.week_no}` },
      ])
    : "";

  const objectives = `<div class="plan-objectives">${cellBlock(rendered.objectives)}${cellBlock(rendered.priorKnowledge)}</div>`;
  const legacy = rendered.legacyProcedure ? cellBlock(rendered.legacyProcedure) : "";

  // SCREEN: stacked flow.
  const screen = `
    <div class="card plan-screen">
      ${objectives}
      <h2 class="plan-proc-title">Lesson Procedure</h2>
      <div class="plan-steps">${rendered.stages.map(stageScreen).join("")}</div>
      ${legacy}
    </div>`;

  // PRINT: the two-column GTIS form (step-title band + Teacher | Student row).
  const printRows = rendered.stages
    .map(
      (s) =>
        `<tr class="step-row"><td colspan="2" class="step-head">${esc(s.title)}</td></tr>
         <tr><td class="td-teacher">${teacherCellPrint(s)}</td><td class="td-student"></td></tr>`,
    )
    .join("");
  const print = `
    <div class="plan-print">
      ${objectives}
      <h2 class="plan-proc-title">Lesson Procedure</h2>
      <table class="plan-table">
        <thead><tr><th>Teacher's Lesson Content</th><th>Student's Activities</th></tr></thead>
        <tbody>${printRows}</tbody>
      </table>
      ${legacy}
      <div class="plan-sign">Teacher's signature: ______________________ &nbsp; Date: ____________</div>
    </div>`;

  app.innerHTML = `
    <div class="topbar">
      <a href="#" id="back" class="pill-btn">${t("common.back")}</a>
      <button class="pill-btn" id="print" type="button">${t("common.print")}</button>
    </div>
    <h1>${data.can_approve ? "Review this plan" : data.can_reopen ? "Approved plan" : t("view.title")}</h1>
    <p class="muted">${esc(lesson.class_name)} · ${esc(lesson.topic)} · ${t("common.week_n", { n: lesson.week_no })}</p>
    ${leadDraft ? `<div id="lead-draft-delete" class="no-print" style="margin-bottom:12px"></div>` : ""}
    ${context}
    ${timingLine(data.timing.duration_sec)}
    <p class="teach-jump no-print"><button type="button" class="linkbtn" id="teach-jump" aria-controls="teaching">${t("teach.jump")} ↓</button>
      <a class="linkbtn" data-link id="session-link" href="/lesson/${esc(data.lesson_id)}?${termQuery}">${t("sess.title")} ›</a></p>
    ${leadNote(lesson.approval_note)}
    <div id="plan-books" class="no-print"></div>

    <p class="no-print"><a class="btn secondary" data-link href="${esc(planningWeekHref(lesson.class_id, lesson.term_id, lesson.week_no, null, lesson.id))}">Back to this week</a></p>
    <section class="card plan-own no-print" id="plan-own" aria-labelledby="po-h">
      <h2 class="card-title" id="po-h">${icon("puzzle")}<span>Materials for this lesson</span></h2>
      <p class="hint">For Drive links, share with the Lead's Google account or school group. The Lead can use Open link to check access; EDEN cannot verify Google permissions.</p>
      <ul class="pr-list" data-plan-own><li>Loading saved materials…</li></ul>
      <button class="btn secondary" type="button" data-plan-retry hidden>Retry loading materials</button>
    </section>
    <section class="card plan-own no-print" id="plan-book-pages" hidden aria-labelledby="pb-h">
      <h2 class="card-title" id="pb-h">${icon("book")}<span>Book pages added to this plan</span></h2>
      <ul class="pr-list" data-plan-books></ul>
    </section>
    ${screen}
    ${print}

    ${driftNote(data.content_changed)}
    <div id="err" class="error" hidden></div>
    <div style="height:8px"></div>
    ${actionArea(data)}

    <section class="card teach-panel no-print" id="teaching" aria-live="polite">
      <p class="muted">${t("teach.loading")}</p>
    </section>`;

  // Back goes where the reader came from (teacher: This Week · Lead: Status/Insights); a fresh
  // deep-link (no in-app history) falls back to This Week so the link always works.
  app.querySelector<HTMLElement>("#back")!.addEventListener("click", (e) => {
    e.preventDefault();
    if (history.length > 1) history.back();
    else navigate(data.can_approve || data.can_reopen ? "/team" : "/plans");
  });

  // Print → a clean paper artifact for the 1-on-1 (the @media print block swaps to .plan-print).
  app.querySelector<HTMLButtonElement>("#print")!.addEventListener("click", () => window.print());

  // Actions are gated server-side (the flags above); the click handlers just call the route and
  // refresh. Submit returns the teacher to This Week; approve/re-open re-render the plan in place.
  const err = app.querySelector<HTMLElement>("#err")!;
  // The Lead's Delete for another teacher's draft: the server decides (a saved worksheet or a teaching record keeps it) and
  // says whether to offer it and what a delete takes with it; the button is two taps, like everywhere else.
  const leadDelete = app.querySelector<HTMLElement>("#lead-draft-delete");
  if (leadDelete) {
    void api<{ can_delete: boolean; delete_note: string | null }>(`/api/lessons/${encodeURIComponent(data.lesson_id)}`)
      .then((info) => {
        if (!info.can_delete) {
          leadDelete.innerHTML = info.delete_note ? `<p class="hint">${esc(info.delete_note)}</p>` : "";
          return;
        }
        leadDelete.innerHTML = `<button class="btn secondary" id="delete-draft" type="button">Delete this draft</button><p class="error" data-lead-err role="alert" hidden></p>`;
        const lateErr = leadDelete.querySelector<HTMLElement>("[data-lead-err]")!;
        wireDraftDelete(leadDelete.querySelector<HTMLButtonElement>("#delete-draft")!, data.lesson_id, {
          ask: `Delete this draft? ${info.delete_note ?? ""} Tap again`.replace(/\s+/g, " "),
          done: () => { toast("Draft deleted"); navigate("/team"); },
          // a refusal that comes late (the teacher saved a worksheet meanwhile) is written under the button, not far down the page
          fail: (message) => { lateErr.textContent = message; lateErr.hidden = false; },
        });
      })
      .catch(() => { /* the plan page works without it; the edit page offers the same Delete */ });
  }
  const act = async (path: string, after: () => void | Promise<void>, body?: unknown): Promise<void> => {
    err.hidden = true;
    try {
      await api(path, { method: "POST", ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
      await after();
    } catch (ex) {
      err.textContent = (ex as Error).message;
      err.hidden = false;
    }
  };
  app.querySelector("#submit")?.addEventListener("click", () => act(`/api/lessons/${lesson.id}/submit`, () => navigate(planningWeekHref(lesson.class_id, lesson.term_id, lesson.week_no, null, lesson.id))));
  const leadNoteValue = (): string | null => app.querySelector<HTMLInputElement>("#lead-note")?.value.trim() || null;
  app.querySelector("#approve")?.addEventListener("click", () => {
    const note = leadNoteValue();
    return act(`/api/lessons/${lesson.id}/approve`, () => renderPlanView(app, params), note ? { note } : undefined);
  });
  app.querySelector("#reopen")?.addEventListener("click", () => {
    const note = leadNoteValue();
    return act(`/api/lessons/${lesson.id}/reopen`, () => renderPlanView(app, params), note ? { note } : undefined);
  });

  // The teaching record sits below the plan; a jump link near the top shows its state and scrolls there.
  const jump = app.querySelector<HTMLButtonElement>("#teach-jump")!;
  jump.addEventListener("click", () => {
    const panel = app.querySelector<HTMLElement>("#teaching");
    panel?.scrollIntoView({ behavior: "smooth", block: "start" });
    // W01 review N3: scrolling alone leaves keyboard and screen-reader focus on a button that has
    // just left the screen. Move focus to the panel's heading so the jump lands for everyone.
    const heading = panel?.querySelector<HTMLElement>(".teach-title");
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
  });
  // The teacher's own activities, links and files (teacher review, point 10): the Lead reads them here, beside the plan.
  // (Not isConnected: the router draws each page off-screen and swaps it in only when render() resolves, so a fast
  // answer would find this box not yet in the document and drop the list. A page the user left is simply discarded.)
  // The book pages added from the Book tab are listed apart: they are the department's books, and the Lead's pages
  // count only the teacher's own activities, links and files.
  const ownBox = app.querySelector<HTMLElement>("#plan-own");
  const bookBox = app.querySelector<HTMLElement>("#plan-book-pages");
  async function reloadMaterials(): Promise<void> {
    const retry = ownBox?.querySelector<HTMLButtonElement>("[data-plan-retry]");
    if (retry) retry.hidden = true;
    const list = await loadResources(lesson.id);
    if (list === null) {
      if (ownBox) ownBox.querySelector<HTMLElement>("[data-plan-own]")!.innerHTML = `<li role="alert">Saved materials could not be loaded. Retry to see the saved list.</li>`;
      if (retry) retry.hidden = false;
      return;
    }
    const own = list.filter((r) => r.kind !== "book");
    const books = list.filter((r) => r.kind === "book");
    if (ownBox) {
      ownBox.querySelector<HTMLElement>("[data-plan-own]")!.innerHTML = own.length ? resourcesListHtml(own) : `<li>No own materials have been added to this plan.</li>`;
      ownBox.hidden = false;
    }
    if (bookBox && books.length) {
      bookBox.querySelector<HTMLElement>("[data-plan-books]")!.innerHTML = resourcesListHtml(books);
      bookBox.hidden = false;
    }
  }
  ownBox?.querySelector("[data-plan-retry]")?.addEventListener("click", () => void reloadMaterials());
  void reloadMaterials();
  // grabbed now, so a slow answer for this plan can never paint into the next plan's page
  const booksHolder = app.querySelector<HTMLElement>("#plan-books");
  await mountTeachingPanel(app.querySelector<HTMLElement>("#teaching")!, lesson.id, (state) => {
    jump.innerHTML = `${t("teach.jump")}: ${teachingStateLabel(state)} ↓`;
    jump.dataset.state = state;
  }, async (d) => {
    // The week's books beside the plan, so the Lead can see at a glance whether the plan uses the
    // right pages (and the teacher has them in reach when they re-open their own plan).
    const ref = await refsFor(d.lesson.node_id);
    if (booksHolder && ref) booksHolder.innerHTML = booksCardHtml(ref, { title: "Books for this lesson" });
  });
}

// ── JOB-W01 · Teaching record ─────────────────────────────────────────────────────────────────────
type TeachingState = "not_recorded" | "taught" | "confirmation_withdrawn";
interface TeachingResponse {
  lesson: { id: string; week_no: number; node_id: string | null };
  curriculum_link: "node" | "freeform";
  planned_week: { week_no: number; starts_on: string | null; started: boolean };
  teaching: {
    state: TeachingState;
    revision: number;
    confirmed_at: string | null;
    withdrawn_at: string | null;
    history: {
      revision: number;
      action: "confirm_taught" | "withdraw_confirmation";
      reason: string | null;
      actor_name: string | null;
      recorded_at: string;
    }[];
  };
  capabilities: {
    can_confirm: boolean;
    can_withdraw: boolean;
    withdraw_reason_max: number;
    confirm_blocked_reason: "week_not_started" | null;
    read_only_reason: "not_owner" | null;
  };
}

// recorded_at is a UTC SQLite datetime ("YYYY-MM-DD HH:MM:SS"); show it on the school's clock.
function schoolTime(utc: string | null): string {
  if (!utc) return "";
  const d = new Date(`${utc.replace(" ", "T")}${/[zZ]|[+-]\d\d:?\d\d$/.test(utc) ? "" : "Z"}`);
  if (Number.isNaN(d.getTime())) return utc;
  return d.toLocaleString("en-GB", {
    timeZone: "Asia/Phnom_Penh", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}
function schoolDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short", year: "numeric" });
}
function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function teachingStateLabel(state: TeachingState): string {
  if (state === "taught") return t("teach.state_taught");
  if (state === "confirmation_withdrawn") return t("teach.state_withdrawn");
  return t("teach.state_not_recorded");
}

function teachingStateChip(state: TeachingState): string {
  if (state === "taught") return `<span class="chip taught">${t("teach.state_taught")}</span>`;
  if (state === "confirmation_withdrawn") return `<span class="chip withdrawn">${t("teach.state_withdrawn")}</span>`;
  return `<span class="chip">${t("teach.state_not_recorded")}</span>`;
}

export async function mountTeachingPanel(
  box: HTMLElement,
  lessonId: string,
  onState: (state: TeachingState) => void = () => {},
  onLoad: (data: TeachingResponse) => void | Promise<void> = () => {},
): Promise<void> {
  const url = `/api/lessons/${encodeURIComponent(lessonId)}/teaching`;
  let data: TeachingResponse;
  try {
    data = await api<TeachingResponse>(url);
    // a side job (the books card): its failure must never touch the teaching record
    const loaded = data;
    void Promise.resolve().then(() => onLoad(loaded)).catch(() => {});
  } catch {
    box.innerHTML = `<h2 class="teach-title">${t("teach.title")}</h2><p class="error" data-teach-error>${t("teach.load_error")}</p>`;
    return;
  }
  // A retry after a dropped connection re-sends the SAME key, so the server replays instead of
  // appending a duplicate. Any server answer (success or refusal) ends that request's key.
  let pending: { action: string; reason: string | null; key: string } | null = null;
  let withdrawing = false;
  let reasonDraft = ""; // survives re-renders (a refused or offline withdrawal keeps the typed reason)
  let message = "";
  let error = "";

  function historyHtml(): string {
    const rows = data.teaching.history;
    if (!rows.length) return `<p class="hint" data-teach-history-empty>${t("teach.history_empty")}</p>`;
    const items = rows
      .map((e) => {
        const what = e.action === "confirm_taught" ? t("teach.event_confirm") : t("teach.event_withdraw");
        const reason = e.reason ? `<div class="teach-reason">“${esc(e.reason)}”</div>` : "";
        return `<li data-revision="${e.revision}"><strong>${what}</strong> <span class="muted">· ${esc(e.actor_name ?? "")} · ${esc(schoolTime(e.recorded_at))}</span>${reason}</li>`;
      })
      .join("");
    return `<details class="teach-history" open><summary>${t("teach.history")} (${rows.length})</summary><ol>${items}</ol></details>`;
  }

  function render(): void {
    const { teaching, capabilities: cap } = data;
    const when =
      teaching.state === "taught"
        ? `<span class="muted">${t("teach.confirmed_on", { when: schoolTime(teaching.confirmed_at) })}</span>`
        : teaching.state === "confirmation_withdrawn"
          ? `<span class="muted">${t("teach.withdrawn_on", { when: schoolTime(teaching.withdrawn_at) })}</span>`
          : "";
    const freeform = data.curriculum_link === "freeform" ? `<p class="hint" data-teach-freeform>${t("teach.freeform")}</p>` : "";
    let controls = "";
    if (cap.read_only_reason === "not_owner") {
      controls = `<p class="hint" data-teach-readonly>${t("teach.read_only")}</p>`;
    } else if (cap.can_withdraw) {
      controls = withdrawing
        ? `<label for="teach-reason" class="teach-reason-label">${t("teach.withdraw_label")}</label>
           <p class="hint">${t("teach.withdraw_hint")}</p>
           <textarea id="teach-reason" maxlength="${cap.withdraw_reason_max}">${esc(reasonDraft)}</textarea>
           <div class="teach-actions">
             <button class="btn secondary sm" type="button" data-teach="withdraw-submit">${t("teach.withdraw_submit")}</button>
             <button class="linkbtn quiet" type="button" data-teach="withdraw-cancel">${t("common.cancel")}</button>
           </div>`
        : `<button class="btn secondary sm" type="button" data-teach="withdraw-open">${t("teach.withdraw")}</button>`;
    } else if (cap.can_confirm) {
      const label = teaching.state === "confirmation_withdrawn" ? t("teach.confirm_again") : t("teach.confirm");
      controls = `<button class="btn moss" type="button" data-teach="confirm">${label}</button>`;
    } else if (cap.confirm_blocked_reason === "week_not_started" && data.planned_week.starts_on) {
      controls = `<p class="hint" data-teach-future>${t("teach.week_not_started", { n: data.planned_week.week_no, date: schoolDate(data.planned_week.starts_on) })}</p>
        <button class="btn moss" type="button" disabled>${t("teach.confirm")}</button>`;
    }
    box.innerHTML = `
      <h2 class="teach-title">${t("teach.title")}</h2>
      <p class="hint">${cap.read_only_reason === "not_owner" ? t("teach.hint_reader") : t("teach.hint")}</p>
      <div class="teach-state" data-teach-state="${teaching.state}">${teachingStateChip(teaching.state)} ${when}</div>
      ${freeform}
      ${message ? `<p class="teach-flash" role="status">${esc(message)}</p>` : ""}
      ${error ? `<p class="error" data-teach-error>${esc(error)}</p>` : ""}
      ${controls}
      ${historyHtml()}`;
    if (withdrawing) box.querySelector<HTMLTextAreaElement>("#teach-reason")?.focus();
    onState(teaching.state);
  }

  async function send(action: "confirm_taught" | "withdraw_confirmation", reason: string | null): Promise<void> {
    if (!pending || pending.action !== action || pending.reason !== reason) {
      pending = { action, reason, key: newIdempotencyKey() };
    }
    const request = pending;
    box.querySelectorAll<HTMLButtonElement>("button").forEach((b) => (b.disabled = true));
    message = "";
    error = "";
    let res: Response;
    try {
      res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action,
          expected_revision: data.teaching.revision,
          idempotency_key: request.key,
          ...(reason === null ? {} : { reason }),
        }),
      });
    } catch {
      error = t("teach.offline"); // keep `pending` — the retry re-uses its key
      render();
      return;
    }
    pending = null;
    const body = (await res.json().catch(() => ({}))) as Partial<TeachingResponse> & { error?: string; current?: TeachingResponse };
    if (res.ok && body.teaching) {
      data = body as TeachingResponse;
      withdrawing = false;
      reasonDraft = "";
      // Say what the record now shows. A replayed old request whose outcome was since changed (e.g. in
      // another tab) must not flash "Recorded as taught" beside a withdrawn state.
      const expected = action === "confirm_taught" ? "taught" : "confirmation_withdrawn";
      message =
        data.teaching.state !== expected
          ? t("teach.replayed_other")
          : action === "confirm_taught"
            ? t("teach.saved_confirm")
            : t("teach.saved_withdraw");
    } else {
      if (body.current) {
        data = body.current; // stale: show the latest record under the explanation
        withdrawing = false;
      }
      error = body.error ?? "Something went wrong.";
    }
    render();
  }

  box.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-teach]");
    if (!el || (el as HTMLButtonElement).disabled) return;
    const what = el.dataset.teach;
    if (what === "confirm") void send("confirm_taught", null);
    if (what === "withdraw-open") { withdrawing = true; message = ""; error = ""; render(); }
    if (what === "withdraw-cancel") { withdrawing = false; reasonDraft = ""; error = ""; render(); }
    if (what === "withdraw-submit") {
      const reason = reasonDraft.trim();
      if (!reason) {
        error = t("teach.reason_required");
        render();
        return;
      }
      void send("withdraw_confirmation", reason);
    }
  });
  box.addEventListener("input", (e) => {
    const field = e.target as HTMLElement;
    if (field.id === "teach-reason") reasonDraft = (field as HTMLTextAreaElement).value;
  });

  render();
}

// The Lead's one-line note from approve/re-open (0017) — read by the TEACHER. A conversation
// opener, never a verdict; quiet accent, never red. Shown wherever a note exists.
function leadNote(note: string | null): string {
  return note
    ? `<div class="lead-note"><span class="eyebrow">${t("view.lead_note")}</span><p>${esc(note)}</p></div>`
    : "";
}

// Optional one-line note the Lead may send with approve/re-open — never required (0017).
function noteField(): string {
  return `<label class="lead-note-field"><span class="eyebrow">${t("view.lead_note_label")}</span>
    <input id="lead-note" maxlength="200" placeholder="${t("view.lead_note_hint")}"></label>`;
}

// A quiet, non-alarming notice — never red. Tells owner + Lead the plan changed after submitting.
function driftNote(changed: boolean): string {
  return changed ? `<p class="muted">${t("view.drift")}</p>` : "";
}

// The action affordances for the current role + status. Neutral copy; Approved reads calm.
function actionArea(d: PlanResponse): string {
  const edit = d.can_edit
    ? `<a class="btn secondary" data-link href="/lesson/${esc(d.lesson_id)}/edit">${t("view.edit")}</a><div style="height:8px"></div>`
    : "";
  if (d.can_submit) {
    const label = d.lesson.status === "submitted" ? t("view.resubmit") : t("view.submit");
    return `${edit}<button class="btn" id="submit" type="button">${label}</button>`;
  }
  if (d.can_approve) return `<p class="muted">${t("view.submitted")}</p>${noteField()}<button class="btn moss" id="approve" type="button">${t("view.approve")}</button>`;
  if (d.can_reopen) return `<p class="muted">${t("view.approved_locked")}</p>${noteField()}<button class="btn secondary" id="reopen" type="button">${t("view.reopen")}</button>`;
  if (d.locked) return `<p class="muted">${t("view.approved_locked")}</p>`;
  if (d.lesson.status === "submitted") return `<p class="muted">${t("view.submitted")}</p>${edit}`;
  return edit;
}
